import { useGetAllManufacturersQuery, useUpdateManufacturerMutation } from '@codezo/graphql';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

export function AssignManufacturerModal({
	open,
	onOpenChange,
	vetterEntityId,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	vetterEntityId: string;
	onSuccess?: () => void;
}) {
	const [draft, setDraft] = useState<string[]>([]);
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);

	const { data, loading } = useGetAllManufacturersQuery({
		variables: {
			input: {
				pagination: { currentPage: page, limit: 20 },
				filter: { unassignedToVetterEntity: true },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: !open,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllManufacturer?.data,
		pageInfo: data?.getAllManufacturer?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	useEffect(() => {
		if (open) {
			setDraft([]);
			setQuery('');
			scroll.reset();
			setPage(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reset selection only when the modal opens
	}, [open]);

	const toggle = (id: string) => {
		setDraft((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
	};

	const [updateManufacturer, { loading: saving }] = useUpdateManufacturerMutation();

	const handleAssign = async () => {
		if (draft.length === 0) return;
		try {
			const results = await Promise.all(
				draft.map((id) =>
					updateManufacturer({ variables: { input: { id, vetterEntityId } } })
				)
			);
			const failed = results.filter((r) => !r.data?.updateManufacturer?.success).length;
			if (failed > 0) {
				toast.error(
					`Assigned ${draft.length - failed} of ${draft.length}. ${failed} failed.`
				);
			} else {
				toast.success(
					draft.length === 1
						? 'Manufacturer assigned'
						: `${draft.length} manufacturers assigned`
				);
			}
			onOpenChange(false);
			onSuccess?.();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to assign manufacturers');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Assign manufacturers</DialogTitle>
					<DialogDescription>
						Select unassigned manufacturers to link to this vetter.
					</DialogDescription>
				</DialogHeader>

				<DialogBody className="flex flex-col gap-0 p-0">
					<div className="relative shrink-0 px-4 pt-4 pb-2">
						<Search
							size={14}
							className="pointer-events-none absolute left-7 top-1/2 -translate-y-1/2 text-text-4"
						/>
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search manufacturers…"
							className="h-8 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-9 pr-3 text-base text-text-1 outline-none placeholder:text-text-4 focus:border-line-3"
							autoFocus
						/>
					</div>

					<div className="scroll-thin h-[260px] overflow-y-auto px-4 py-1">
						<div className="flex flex-col gap-0.5">
							{scroll.items.map((mfr) => {
								const isSelected = draft.includes(mfr.id);
								return (
									<button
										key={mfr.id}
										onClick={() => toggle(mfr.id)}
										className={cn(
											'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover',
											isSelected && 'bg-surface-fill'
										)}
									>
										<Checkbox
											checked={isSelected}
											tabIndex={-1}
											className="pointer-events-none"
										/>
										<InitialsAvatar
											name={mfr.name}
											src={mfr.logoUrl}
											size={28}
											shape="rounded"
										/>
										<div className="min-w-0 flex-1">
											<div className="truncate text-base font-medium text-text-1">
												{mfr.name}
											</div>
											{mfr.country && (
												<div className="truncate text-xs text-text-4">
													{mfr.country}
												</div>
											)}
										</div>
									</button>
								);
							})}
							{scroll.initialLoading && (
								<div className="flex items-center justify-center py-6 text-sm text-text-4">
									Loading…
								</div>
							)}
							{!loading && scroll.items.length === 0 && !scroll.initialLoading && (
								<div className="flex items-center justify-center py-6 text-center text-sm text-text-4">
									{query
										? 'No manufacturers match your search.'
										: 'No unassigned manufacturers available.'}
								</div>
							)}
							{scroll.loadingMore && (
								<div className="py-2 text-center text-xs text-text-4">
									Loading more…
								</div>
							)}
							<div ref={scroll.sentinelRef} className="h-1" />
						</div>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={handleAssign} disabled={saving || draft.length === 0}>
						{saving ? 'Assigning…' : `Assign (${draft.length})`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
